import { useMemo, useRef, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TASK_CATEGORIES } from '@/components/staff/StaffTasksAdmin';
import { toast } from 'sonner';
import { Check, Users } from 'lucide-react';

const RECUR_OPTS = [
  { v: 'none', label: 'One-time (no expiry)' },
  { v: '3', label: 'Re-sign every 3 months' },
  { v: '6', label: 'Re-sign every 6 months' },
  { v: '12', label: 'Re-sign every 12 months' },
];
const EMPTY = { title: '', description: '', category: 'Training', dueDate: '', completionType: 'SIGN', recurMonths: 'none', signatureMethods: 'BOTH' };
const SIG_METHODS = [
  { v: 'BOTH', label: 'Type or draw (both allowed)' },
  { v: 'TYPED', label: 'Typed name only' },
  { v: 'DRAWN', label: 'Drawn signature only' },
];

// Assign one activity to many staff at once — pick individual (active) staff and/or whole job roles.
export function BulkAssignActivityDialog({ open, onOpenChange, staff, roles, onDone }) {
  const [f, setF] = useState(EMPTY);
  const [pickedStaff, setPickedStaff] = useState([]);
  const [pickedRoles, setPickedRoles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const activeStaff = useMemo(() => staff.filter((s) => s.isActive), [staff]);

  // Effective recipients = picked staff ∪ (active staff holding any picked role).
  const recipientIds = useMemo(() => {
    const ids = new Set(pickedStaff);
    if (pickedRoles.length) {
      for (const s of activeStaff) {
        if ((s.jobRoles || []).some((r) => pickedRoles.includes(r.id))) ids.add(s.id);
      }
    }
    return [...ids];
  }, [pickedStaff, pickedRoles, activeStaff]);

  const reset = () => { setF(EMPTY); setPickedStaff([]); setPickedRoles([]); if (fileRef.current) fileRef.current.value = ''; };

  const toggleStaff = (id) => setPickedStaff((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleRole = (id) => setPickedRoles((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (!f.title.trim()) return toast.error('Enter a title');
    if (recipientIds.length === 0) return toast.error('Select at least one staff member or role');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('title', f.title.trim());
      if (f.description.trim()) form.append('description', f.description.trim());
      form.append('category', f.category);
      if (f.dueDate) form.append('dueDate', f.dueDate);
      form.append('completionType', f.completionType);
      if (f.completionType === 'SIGN' && f.recurMonths !== 'none') form.append('recurMonths', f.recurMonths);
      if (f.completionType === 'SIGN') form.append('signatureMethods', f.signatureMethods);
      form.append('staffIds', JSON.stringify(pickedStaff));
      form.append('roleIds', JSON.stringify(pickedRoles));
      const file = fileRef.current?.files?.[0];
      if (file) form.append('sourceFile', file);
      const { data } = await staffApi.post('/staff/tasks/bulk', form);
      toast.success(`Assigned to ${data.count} staff member${data.count === 1 ? '' : 's'}`);
      onOpenChange(false);
      reset();
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="bulk-assign-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk Assign Activity</DialogTitle>
          <DialogDescription>Assign the same task, document or sign-off to several staff at once.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="bulk-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Food Safety Course 2026" />
          </div>
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Textarea data-testid="bulk-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="bulk-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Due date (optional)</Label>
              <Input type="date" data-testid="bulk-due-input" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>How is it completed?</Label>
            <Select value={f.completionType} onValueChange={(v) => setF({ ...f, completionType: v })}>
              <SelectTrigger data-testid="bulk-completion-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ACKNOWLEDGE">One-tap acknowledge</SelectItem>
                <SelectItem value="FILE">Upload a file</SelectItem>
                <SelectItem value="SIGN">Read &amp; sign</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.completionType === 'SIGN' && (
            <div className="space-y-1.5">
              <Label>Re-signing</Label>
              <Select value={f.recurMonths} onValueChange={(v) => setF({ ...f, recurMonths: v })}>
                <SelectTrigger data-testid="bulk-recur-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RECUR_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {f.completionType === 'SIGN' && (
            <div className="space-y-1.5">
              <Label>Signature method</Label>
              <Select value={f.signatureMethods} onValueChange={(v) => setF({ ...f, signatureMethods: v })}>
                <SelectTrigger data-testid="bulk-sigmethod-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SIG_METHODS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{f.completionType === 'SIGN' ? 'Document to read before signing (optional)' : 'Attach a document (optional)'}</Label>
            <input
              ref={fileRef}
              type="file"
              data-testid="bulk-source-file-input"
              className="block w-full text-sm text-charcoal file:mr-3 file:rounded-lg file:border-0 file:bg-saffron file:px-3 file:py-1.5 file:text-white file:text-sm"
            />
          </div>

          <div className="space-y-2 pt-1 border-t border-border/40">
            <Label className="flex items-center gap-1.5"><Users size={14} /> Recipients — {recipientIds.length} selected</Label>
            {roles.length > 0 && (
              <div>
                <p className="text-xs text-muted-ink mb-1">By job role (adds all active staff with that role)</p>
                <div className="flex flex-wrap gap-1.5">
                  {roles.map((r) => {
                    const on = pickedRoles.includes(r.id);
                    return (
                      <button type="button" key={r.id} data-testid={`bulk-role-${r.id}`} onClick={() => toggleRole(r.id)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? 'bg-saffron text-white border-saffron' : 'bg-white text-charcoal border-border/60 hover:border-saffron/60'}`}>
                        {on && <Check size={12} />}{r.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-ink mb-1">Individual staff</p>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-border/40 divide-y divide-border/30" data-testid="bulk-staff-list">
                {activeStaff.map((s) => {
                  const viaRole = pickedRoles.length && (s.jobRoles || []).some((r) => pickedRoles.includes(r.id));
                  const checked = pickedStaff.includes(s.id) || viaRole;
                  return (
                    <label key={s.id} data-testid={`bulk-staff-${s.id}`} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${viaRole ? 'opacity-70' : ''}`}>
                      <input type="checkbox" checked={checked} disabled={viaRole} onChange={() => toggleStaff(s.id)} className="h-4 w-4 accent-saffron" />
                      <span className="text-charcoal">{s.name}</span>
                      <span className="text-xs text-muted-ink">{s.role}</span>
                      {viaRole && <span className="text-[10px] text-saffron ml-auto">via role</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="bulk-assign-submit-btn" disabled={busy || recipientIds.length === 0} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
            {busy ? 'Assigning…' : `Assign to ${recipientIds.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
